import {
  ObservableObject,
  observableValue,
  ObservableValue,
  WritableObservableValue,
} from '../shared-state/index.js';
import { Clazz } from '../view.js';
import { useContext } from './use-context.js';
import { useEffect } from './use-effect.js';
import { useState } from './use-state.js';

export function useEnvironmentObject<T extends ObservableObject>(
  O: Clazz<T>
): T | undefined;

export function useEnvironmentObject<T extends ObservableObject>(
  O: Clazz<T>,
  defaultValue: T
): T;

export function useEnvironmentObject<T extends ObservableObject>(
  O: Clazz<T>,
  defaultValue?: T
) {
  const ctx = useContext<T>((O as any).key());
  return ctx ?? defaultValue;
}

export function useObservedValue<T>(observedValue: ObservableValue<T> | T) {
  const initialValue =
    observedValue instanceof ObservableValue
      ? observedValue.value
      : observedValue;
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (observedValue instanceof ObservableValue) {
      return observedValue.subscribe(setValue);
    }
    setValue(observedValue);
  }, [observedValue]);

  return value;
}

export function useObservedValues<T>(
  observedValues: (ObservableValue<T> | T)[]
) {
  return observedValues.map(observedValue => useObservedValue(observedValue));
}

export function useSharableState<T>(initialValue: T) {
  const [stateValue] = useState(observableValue(initialValue));

  const [, setValue] = useState(initialValue);
  useEffect(() => stateValue.subscribe(setValue), [stateValue]);

  return stateValue;
}

export function useSharedState<T>(sharedState: WritableObservableValue<T>) {
  const [, setValue] = useState(sharedState.value);

  useEffect(() => sharedState.subscribe(setValue), [sharedState]);

  return sharedState;
}

export function useSharableStates<T, U>(
  tValue: T,
  uValue: U
): [WritableObservableValue<T>, WritableObservableValue<U>];
export function useSharableStates<T, U, V>(
  tValue: T,
  uValue: U,
  vValue: V
): [
  WritableObservableValue<T>,
  WritableObservableValue<U>,
  WritableObservableValue<V>
];
export function useSharableStates<T, U, V, W>(
  tValue: T,
  uValue: U,
  vValue: V,
  wValue: W
): [
  WritableObservableValue<T>,
  WritableObservableValue<U>,
  WritableObservableValue<V>,
  WritableObservableValue<W>
];
export function useSharableStates<T, U, V, W, Z>(
  tValue: T,
  uValue: U,
  vValue: V,
  wValue: W,
  zValue: Z
): [
  WritableObservableValue<T>,
  WritableObservableValue<U>,
  WritableObservableValue<V>,
  WritableObservableValue<W>,
  WritableObservableValue<Z>
];
export function useSharableStates(
  ...initialValues: unknown[]
): WritableObservableValue<unknown>[];
export function useSharableStates(...initialValues: any[]) {
  return initialValues.map(initialValue => useSharableState(initialValue));
}

export function useSharedStates<T, U>(
  tValue: WritableObservableValue<T>,
  uValue: WritableObservableValue<U>
): [WritableObservableValue<T>, WritableObservableValue<U>];
export function useSharedStates<T, U, V>(
  tValue: WritableObservableValue<T>,
  uValue: WritableObservableValue<U>,
  vValue: WritableObservableValue<V>
): [
  WritableObservableValue<T>,
  WritableObservableValue<U>,
  WritableObservableValue<V>
];
export function useSharedStates<T, U, V, W>(
  tValue: WritableObservableValue<T>,
  uValue: WritableObservableValue<U>,
  vValue: WritableObservableValue<V>,
  wValue: WritableObservableValue<W>
): [
  WritableObservableValue<T>,
  WritableObservableValue<U>,
  WritableObservableValue<V>,
  WritableObservableValue<W>
];
export function useSharedStates<T, U, V, W, Z>(
  tValue: WritableObservableValue<T>,
  uValue: WritableObservableValue<U>,
  vValue: WritableObservableValue<V>,
  wValue: WritableObservableValue<W>,
  zValue: WritableObservableValue<Z>
): [
  WritableObservableValue<T>,
  WritableObservableValue<U>,
  WritableObservableValue<V>,
  WritableObservableValue<W>,
  WritableObservableValue<Z>
];
export function useSharedStates(
  ...initialValues: WritableObservableValue<unknown>[]
): WritableObservableValue<unknown>[];
export function useSharedStates(
  ...sharedStates: WritableObservableValue<unknown>[]
): WritableObservableValue<unknown>[] {
  return sharedStates.map(sharedState => useSharedState(sharedState));
}
